import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import MicroModal from '../shared/Modal';
import { R } from '@/Resources.resx';
import classNames from 'classnames';

const containers: { [id: string]: HTMLElement } = {};
let _modalContainer: HTMLElement | null;
function getModalContainer(id: string) {
    if (_modalContainer == null) {
        _modalContainer = document.createElement('div');
        document.body.appendChild(_modalContainer);
    }

    let container = containers[id];
    if (container == null) {
        container = document.createElement('div');
        container.id = id;
        container.className = 'modal';
        container.ariaHidden = 'true';
        containers[id] = container;
        _modalContainer.appendChild(container);
    }

    return container;
}

type Props = {
    title?: string;
    hideCloseButton?: boolean;
    showCloseButtonInRightCorner?: boolean;
    onClose: () => void;
    children: React.ReactNode;
    containerClassName?: string;
    className?: string;
};
export function Modal({
    title,
    hideCloseButton = false,
    showCloseButtonInRightCorner = false,
    containerClassName,
    className,
    onClose,
    children,
}: Props) {
    const [id, setID] = useState(crypto.randomUUID());
    const container = getModalContainer(id);
    useEffect(() => {
        MicroModal.show(id, () => {
            onClose();
            setID(crypto.randomUUID());
        });
        return () => {
            MicroModal.close(id);
            container.remove();
        };
    }, [id]);

    const modal = (
        <div
            className={classNames(
                'modal__overlay fixed bottom-0 left-0 right-0 top-0 z-[1] flex items-start justify-center',
                className,
            )}
            style={{ background: 'rgba(0,0,0,0.6)' }}
            tabIndex={-1}
        >
            <div
                className={classNames(
                    'modal__container mx-4 mt-6 w-full max-w-[30em] rounded-xl bg-white p-6 ns:p-10',
                    containerClassName,
                )}
                style={{ maxHeight: '100vh', overflowY: 'auto' }}
                role="dialog"
                aria-modal="true"
                aria-labelledby={id + '-title'}
            >
                <header className="mb-4 flex flex-row-reverse items-center justify-between">
                    {hideCloseButton || (
                        <button
                            className={classNames(
                                'modal__close cursor-pointer border-0 bg-transparent',
                                {
                                    'ml-auto': showCloseButtonInRightCorner,
                                },
                            )}
                            aria-label={R.Close}
                            data-micromodal-close
                        >
                            &#10005;
                        </button>
                    )}
                    {title && (
                        <h4 className="m-0 leading-tight" id={id + '-title'}>
                            {title}
                        </h4>
                    )}
                </header>
                {children}
            </div>
        </div>
    );

    return createPortal(modal, container);
}
